<script>
import dayjs from 'dayjs';
import { getCalendarEvents } from '@/helpers/api/launchpad';
import CalendarCard from '@/components/Launchpad/CalendarCard';
import Button from '@/components/Button';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import weekday from 'dayjs/plugin/weekday';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(weekday);
dayjs.extend(isBetween);

export default {
  name: 'Calendar',
  components: {
    CalendarCard,
    Button,
  },
  data() {
    return {
      daysOfWeek: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      selectedMonth: dayjs().format('YYYY-MM'),
      days: [],
      events: [],
    };
  },
  async created() {
    this.events = await getCalendarEvents();
    this.init();
  },
  computed: {},
  methods: {
    dayjs,
    init() {
      const days = [...this.previousMonth(), ...this.thisMonth(), ...this.nextMonth()];

      const startDate = this.previousMonth().length
        ? this.previousMonth()[0]?.date
        : this.thisMonth()[0]?.date;

      const finishDate = this.nextMonth().length
        ? this.nextMonth().slice(-1)[0]?.date
        : this.thisMonth()[this.daysInMonth() - 1]?.date;

      this.events.forEach((item) => {
        if (dayjs(item.date, 'DD.MM.YYYY').isBetween(startDate, finishDate)) {
          days.forEach((d, i) => {
            if (dayjs(d.date).isSame(dayjs(item.date, 'DD.MM.YYYY'))) {
              days[i].events.push(item);
            }
          });
        }
      });

      this.days = [...Array(days.length / 7)].map(() => days.splice(0, 7));
    },
    daysInMonth() {
      return dayjs(this.selectedMonth).daysInMonth();
    },
    weekDayLast() {
      return dayjs(this.selectedMonth)
        .date(this.daysInMonth())
        .weekday();
    },
    weekDayFirst() {
      return dayjs(this.selectedMonth)
        .date(1)
        .weekday();
    },
    thisMonth() {
      return Array.from({ length: this.daysInMonth() }, (_, i) => ({
        date: dayjs(this.selectedMonth)
          .date(i + 1)
          .format('YYYY-MM-DD'),
        currentMonth: true,
        today: dayjs().isSame(dayjs(this.selectedMonth).date(i + 1), 'day'),
        events: [],
        isUpcoming: dayjs().isBefore(dayjs(this.selectedMonth).date(i + 1), 'day'),
      }));
    },
    previousMonth() {
      const month = dayjs(this.selectedMonth).month();
      const prevMonth = month ? month - 1 : 11;
      const daysInPrevMonth = dayjs(this.selectedMonth)
        .month(prevMonth)
        .daysInMonth();
      return this.weekDayFirst()
        ? Array.from({ length: this.weekDayFirst() }, (_, i) => ({
          date: dayjs(this.selectedMonth)
            .month(prevMonth)
            .date(daysInPrevMonth - (this.weekDayFirst() - (i + 1)))
            .format('YYYY-MM-DD'),
          currentMonth: false,
          today: dayjs().isSame(
            dayjs(this.selectedMonth)
              .month(prevMonth)
              .date(daysInPrevMonth - (this.weekDayFirst() - (i + 1))),
            'day',
          ),
          events: [],
          isUpcoming: dayjs().isBefore(
            dayjs(this.selectedMonth)
              .month(prevMonth)
              .date(daysInPrevMonth - (this.weekDayFirst() - (i + 1))),
            'day',
          ),
        }))
        : [];
    },
    nextMonth() {
      const month = dayjs(this.selectedMonth).month();
      const nextMonth = month === 11 ? 0 : month + 1;
      return this.weekDayLast() !== 6
        ? Array.from({ length: 6 - this.weekDayLast() }, (_, i) => ({
          date: dayjs(this.selectedMonth)
            .month(nextMonth)
            .date(i + 1)
            .format('YYYY-MM-DD'),
          currentMonth: false,
          today: dayjs().isSame(
            dayjs(this.selectedMonth)
              .month(nextMonth)
              .date(i + 1),
            'day',
          ),
          events: [],
          isUpcoming: dayjs().isBefore(
            dayjs(this.selectedMonth)
              .month(nextMonth)
              .date(i + 1),
            'day',
          ),
        }))
        : [];
    },
    decrementMonth() {
      this.selectedMonth = dayjs(this.selectedMonth)
        .subtract(1, 'month')
        .format('YYYY-MM');
      this.init();
    },
    incrementMonth() {
      this.selectedMonth = dayjs(this.selectedMonth)
        .add(1, 'month')
        .format('YYYY-MM');
      this.init();
    },
    today() {
      this.selectedMonth = dayjs().format('YYYY-MM');
      this.init();
    },
  },
};
</script>

<template>
  <div class="calendar page-content main-wrap">
    <h1 class="calendar__title">{{ $t('calendar.title') }}</h1>
    <div class="calendar__selector">
      <Button class="btn--round" @click="decrementMonth">&larr;</Button>
      <div class="calendar__selector-date">{{ dayjs(selectedMonth).format('MMMM YYYY') }}</div>
      <Button class="btn--round" @click="incrementMonth">&rarr;</Button>
      <Button class="btn--small" @click="today">Today</Button>
    </div>
    <div class="calendar__row mb-5">
      <div class="calendar__item calendar__days" v-for="(item, index) in daysOfWeek" :key="index">
        {{ item }}
      </div>
    </div>
    <div class="calendar__row mb-5" v-for="(row, index) in days" :key="`row-${index}`">
      <div class="calendar__item" v-for="(day, idx) in row" :key="`row-${index}-${idx}`">
        <CalendarCard
          :disabled="!day.currentMonth"
          :date="day.date"
          :events="day.events"
          :today="day.today"
          :isUpcoming="day.isUpcoming"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.calendar {
  &__row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    gap: 2rem;
  }

  &__item {
    flex-grow: 1;
    flex-basis: 0;
  }

  &__title {
    font-size: 4.4rem;
    margin-bottom: 2rem;
    font-weight: 700;
  }

  &__days {
    font-weight: 800;
    font-size: 3.2rem;
    text-align: center;

    @media screen and (max-width: $breakpoint-lg) {
      font-size: 2rem;
    }
  }

  &__selector {
    display: flex;
    align-items: center;
    font-size: 1.8rem;
    padding-bottom: 3rem;

    &-date {
      text-transform: uppercase;
      font-weight: 700;
      min-width: 20rem;
      text-align: center;
    }

    .btn--round {
      padding: 0.5rem;
      width: 5rem;
      height: 5rem;
    }

    .btn--small {
      height: 5rem;
      padding: 0.5rem 5rem;
      margin-left: 5rem;
    }
  }
}
</style>
